import { useEffect, useId, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useResourceKit } from '@resource-kit/core/react';
import { cn } from '../../cn';
import { useReferenceLabels, useReferenceSearch } from '@resource-kit/controllers/react';

export interface RelationInputProps {
  /** Declared name of the target model — the registry key. */
  model: string;
  /** Current draft value: the foreign id, or the empty encoding. */
  value: unknown;
  onChange: (next: unknown) => void;
  onBlur?: () => void;
  readOnly?: boolean;
  fieldId: string;
  fieldLabel: string;
  invalid?: boolean;
  describedBy?: string;
}

/** Long enough that a typist does not fire a request per keystroke. */
const SEARCH_DEBOUNCE_MS = 250;

/**
 * The EDIT half of a many2one field (design §5.11).
 *
 * This is a remote-search select, and it must be: the operator types "Acme" and
 * holds no id, so candidates cannot come from the batch id read the read-only
 * half uses. Two different endpoints for the two modes is the invariant, not an
 * implementation detail.
 *
 * The control is the ARIA combobox pattern with a non-focusable listbox:
 * keyboard focus never leaves the text field, and the active option is named
 * through `aria-activedescendant`. Picking writes the ID back — never the label
 * — and clearing writes the empty string, which the field's `encodeEmpty`
 * policy turns into whatever the endpoint accepts for "cleared".
 */
export function RelationInput({
  model,
  value,
  onChange,
  onBlur,
  readOnly = false,
  fieldId,
  fieldLabel,
  invalid = false,
  describedBy,
}: RelationInputProps) {
  const { t } = useResourceKit();
  const listId = useId();
  const optionPrefix = useId();

  const id = typeof value === 'string' && value !== '' ? value : null;
  const ids = useMemo(() => (id === null ? [] : [id]), [id]);
  const { labels } = useReferenceLabels(model, ids);

  /** `null` = not typing; the field then shows the current selection. */
  const [query, setQuery] = useState<string | null>(null);
  const [needle, setNeedle] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setNeedle(query ?? ''), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const { candidates, isPending } = useReferenceSearch(model, needle);
  const open = query !== null && query.trim() !== '';
  // Between a keystroke and the debounce firing, the search has NOT run for
  // what is on screen. Without this, that window renders "no matches" — an
  // answer to a question nobody asked yet.
  const settling = (query ?? '').trim() !== needle.trim();
  const selectedLabel = id === null ? '' : (labels.get(id) ?? id);

  const pick = (candidateId: string) => {
    setQuery(null);
    onChange(candidateId);
  };

  const clear = () => {
    setQuery(null);
    onChange('');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || candidates.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => (i + 1) % candidates.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => (i - 1 + candidates.length) % candidates.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const candidate = candidates[Math.min(activeIndex, candidates.length - 1)];
      if (candidate) pick(candidate.id);
    } else if (event.key === 'Escape') {
      setQuery(null);
    }
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-1">
        <input
          id={fieldId}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={
            open && candidates.length > 0 ? `${optionPrefix}-${activeIndex}` : undefined
          }
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          readOnly={readOnly}
          value={query ?? selectedLabel}
          placeholder={t('rk.resource.selectField', { field: fieldLabel })}
          onChange={(event) => {
            setActiveIndex(0);
            setQuery(event.target.value);
          }}
          onKeyDown={handleKeyDown}
          onBlur={onBlur}
          className={cn(
            'flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm',
            'ring-offset-background placeholder:text-muted-foreground',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            'read-only:cursor-not-allowed read-only:opacity-50',
            invalid ? 'border-destructive' : 'border-input',
          )}
        />
        {!readOnly && (id !== null || query !== null) && (
          <button
            type="button"
            aria-label={t('rk.resource.relation.clear', { field: fieldLabel })}
            onClick={clear}
            className="shrink-0 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-label={fieldLabel}
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-border bg-popover p-1 shadow-md"
        >
          {candidates.map((candidate, index) => (
            <li
              key={candidate.id}
              id={`${optionPrefix}-${index}`}
              role="option"
              aria-selected={candidate.id === id}
              // `mousedown` fires before the input's blur, so the
              // pick lands instead of being cancelled by a close.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => pick(candidate.id)}
              className={cn(
                'cursor-pointer rounded px-2 py-1.5 text-sm',
                index === activeIndex ? 'bg-accent/10' : 'hover:bg-muted',
              )}
            >
              {candidate.label}
            </li>
          ))}
          {candidates.length === 0 && !isPending && !settling && (
            <li className="px-2 py-1.5 text-sm text-muted-foreground">
              {t('rk.resource.relation.noMatches')}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
